import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  IComplianceHealthStats,
  IEntityDashboardStats,
  IEntityQualityMetrics,
  IGroupStats,
  IHighRiskEntityWeeklyStats,
  IProductDashboardStats,
} from './stats';
import {
  getComplianceHealth,
  getGroupEntityDashboardStats,
  getGroupStats,
  getEntityQualityMetrics,
  getHighRiskEntitiesThisWeek,
  getProductStats,
  getDatabaseStats,
  getDatabaseProductStats,
  getGroupProductDbStats,
  getProductIssuesByPriority,
  getGroupRecentActivityStats,
} from './stats.service';

// Query key constants
export const STATS_QUERY_KEYS = {
  stats: 'STATS',
  productStats: 'PRODUCT STATS',
  productDbStats: 'PRODUCT DB STATS',
  entityQuality: 'ENTITY_QUALITY',
  highRiskWeekly: 'HIGH_RISK_WEEKLY',
  complianceHealth: 'COMPLIANCE_HEALTH',
  entityDashboard: 'ENTITY_DASHBOARD',
} as const;

const statsKeys = {
  byGroup: (group: string) => [STATS_QUERY_KEYS.stats, group] as const,
  byGroupPriority: (group: string, page?: number, pageSize?: number) =>
    [STATS_QUERY_KEYS.stats, group, 'priority', page, pageSize] as const,
  byEntityDashboard: (group: string) =>
    [STATS_QUERY_KEYS.stats, group, STATS_QUERY_KEYS.entityDashboard] as const,
  byEntityQuality: (group: string) =>
    [STATS_QUERY_KEYS.entityQuality, group] as const,
  byHighRiskWeekly: (group: string) =>
    [STATS_QUERY_KEYS.highRiskWeekly, group] as const,
  byComplianceHealth: (group: string) =>
    [STATS_QUERY_KEYS.complianceHealth, group] as const,
  byGroupProduct: (group: string) =>
    [STATS_QUERY_KEYS.productStats, group] as const,
  byGroupProductDb: (group: string) =>
    [STATS_QUERY_KEYS.productDbStats, group] as const,
  byDatabase: (database: string) => [STATS_QUERY_KEYS.stats, database] as const,
  byDatabaseProduct: (database: string, page?: number, pageSize?: number) =>
    [STATS_QUERY_KEYS.productStats, database, page, pageSize] as const,
};

// Entity Hub stats are refreshed by invalidation after writes and screening
// phases; the stale time only bounds how old a revisited view can be.
const ENTITY_STATS_STALE_TIME = 60 * 1000;

const hasValidGroup = (group: string) =>
  Boolean(group) && group !== 'EMPTY_TEAM';

export const useGetProductIssuesByPriority = (
  group: string,
  page: number,
  pageSize: number,
): UseQueryResult<IProductDashboardStats['IssuesByPriority'] | null> => {
  return useQuery({
    queryKey: statsKeys.byGroupPriority(group, page, pageSize),
    queryFn: () => getProductIssuesByPriority(group, page, pageSize),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: hasValidGroup(group),
  });
};

export const useGetStatsByGroupId = (
  group: string,
): UseQueryResult<IGroupStats | null> => {
  return useQuery({
    queryKey: statsKeys.byGroup(group),
    queryFn: () => getGroupStats(group),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: hasValidGroup(group),
  });
};

export const useGetEntityDashboardStatsByGroupId = (
  group: string,
): UseQueryResult<IEntityDashboardStats | null> => {
  return useQuery({
    queryKey: statsKeys.byEntityDashboard(group),
    queryFn: () => getGroupEntityDashboardStats(group),
    staleTime: ENTITY_STATS_STALE_TIME,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: hasValidGroup(group),
  });
};

export const useGetStatsByDatabaseId = (
  database: string,
): UseQueryResult<IGroupStats | null> => {
  return useQuery({
    queryKey: statsKeys.byDatabase(database),
    queryFn: () => getDatabaseStats(database),
    staleTime: ENTITY_STATS_STALE_TIME,
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

export const useGetEntityQualityMetricsByGroupId = (
  group: string,
): UseQueryResult<IEntityQualityMetrics | null> => {
  return useQuery({
    queryKey: statsKeys.byEntityQuality(group),
    queryFn: () => getEntityQualityMetrics(group),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: hasValidGroup(group),
  });
};

export const useGetHighRiskEntitiesThisWeekByGroupId = (
  group: string,
): UseQueryResult<IHighRiskEntityWeeklyStats | null> => {
  return useQuery({
    queryKey: statsKeys.byHighRiskWeekly(group),
    queryFn: () => getHighRiskEntitiesThisWeek(group),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: hasValidGroup(group),
  });
};

export const useGetComplianceHealthByGroupId = (
  group: string,
): UseQueryResult<IComplianceHealthStats | null> => {
  return useQuery({
    queryKey: statsKeys.byComplianceHealth(group),
    queryFn: () => getComplianceHealth(group),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: hasValidGroup(group),
  });
};

export const useGetProductStatsByGroupId = (
  group: string,
  priorityPage: number,
  priorityPageSize: number,
): UseQueryResult<IProductDashboardStats | null> => {
  return useQuery({
    queryKey: statsKeys.byGroupProduct(group),
    queryFn: () => getProductStats(group, priorityPage, priorityPageSize),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: hasValidGroup(group),
  });
};

export const useGetProductDbStatsByGroupId = (
  group: string,
): UseQueryResult<Partial<IGroupStats> | null> => {
  return useQuery({
    queryKey: statsKeys.byGroupProductDb(group),
    queryFn: () => getGroupProductDbStats(group),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: hasValidGroup(group),
  });
};


export const useGetGroupRecentActivityStats = (
  group: string,
): UseQueryResult<any[] | null> => {
  return useQuery({
    queryKey: [...statsKeys.byGroup(group), 'recent-sctivity'] as const,
    queryFn: () => getGroupRecentActivityStats(group),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: hasValidGroup(group),
  });
};

export const useGetProductStatsByDatabaseId = (
  database: string,
  priorityPage: number,
  priorityPageSize: number,
): UseQueryResult<IProductDashboardStats | null> => {
  return useQuery({
    queryKey: statsKeys.byDatabaseProduct(
      database,
      priorityPage,
      priorityPageSize,
    ),
    queryFn: () =>
      getDatabaseProductStats(database, priorityPage, priorityPageSize),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
